import { useEffect, useMemo, useState } from 'react'
import { CircularProgress, IconButton, Stack, Tooltip } from '@mui/material'

import { InlinePopoverEditor } from './InlinePopoverEditor'
import { AIGenerateIcon } from './RepeatTranslation'

import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { debounce } from '@/hooks/useDebounce'
import useUtils from '@/hooks/useUtilities'
import generateCorrectAnswer from '@/utils/generateCorrectAnswer'

interface CorrectAnswerProps {
  card: CardActivityWithId
  updateCard: (id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => void
}

export default function CorrectAnswer({ card, updateCard }: CorrectAnswerProps) {
  const { showSnackbar } = useUtils()
  const [loadingCorrectAnswer, setLoadingCorrectAnswer] = useState(false)
  const debouncedUpdateCard = useMemo(() => {
    return debounce((id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => {
      updateCard(id, field, value)
    }, 500)
  }, [updateCard])

  const handleChange = (value: string) => {
    debouncedUpdateCard(card.id, 'correct_answer', value)
  }

  const handleGenerateCorrectAnswerWithAI = async () => {
    try {
      setLoadingCorrectAnswer(true)
      const response = await generateCorrectAnswer({
        question: card?.prompt || '',
        characterLimit: card?.maxCharacters || 0,
        language: card?.language || 'en',
      })

      if ('success' in response && response.success && 'correct_answer' in response) {
        updateCard(card.id, 'correct_answer', response.correct_answer)
        // @ts-ignore - showSnackbar function signature varies across components
        showSnackbar('Correct answer generated successfully', 'success')
      }
    } catch (error) {
      console.error(error)
      // @ts-ignore - showSnackbar function signature varies across components
      showSnackbar('Error generating correct answer', 'error')
    } finally {
      setLoadingCorrectAnswer(false)
    }
  }

  useEffect(() => {
    return () => {
      debouncedUpdateCard.cancel?.()
    }
  }, [debouncedUpdateCard])

  return (
    <Stack
      alignItems="center"
      direction="row"
      gap="4px"
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Tooltip title={loadingCorrectAnswer ? 'Generating Correct Answer...' : 'Generate Correct Answer'}>
        <span>
          <IconButton disabled={loadingCorrectAnswer} onClick={handleGenerateCorrectAnswerWithAI}>
            {loadingCorrectAnswer ? <CircularProgress size={21} /> : <AIGenerateIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <InlinePopoverEditor
        label={card?.correct_answer || 'Click to edit'}
        placeholder="Enter here..."
        showUnderline={false}
        value={card?.correct_answer || ''}
        onSave={newValue => {
          handleChange(newValue)
        }}
      />
    </Stack>
  )
}
